import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Page, PageHeader } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ROLE_LABELS, fmtDate } from "@/lib/labels";
import PhoneInput from "@/components/PhoneInput";
import EmployeeCard from "@/components/EmployeeCard";
import { Plus, Trash2, Pencil, X } from "lucide-react";
import { toast } from "sonner";

function EditUserDialog({ user, onSaved, deps, locs }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({});
  useEffect(() => {
    if (open) setF({ name: user.name || "", email: user.email || "", phone: user.phone || "", role: user.role, department: user.department || "", location: user.location || "", weekly_capacity: user.weekly_capacity ?? 37.5, annual_salary: user.annual_salary ?? "", password: "" });
  }, [open, user]);
  const save = async () => {
    const payload = { name: f.name, phone: f.phone, role: f.role, department: f.department, location: f.location, weekly_capacity: Number(f.weekly_capacity) || 37.5 };
    if (f.annual_salary !== "" && f.annual_salary != null) payload.annual_salary = Number(f.annual_salary);
    if (f.password) payload.password = f.password;
    if (f.email && f.email.trim().toLowerCase() !== (user.email || "").toLowerCase()) payload.email = f.email.trim().toLowerCase();
    try { await api.put(`/auth/users/${user.id}`, payload); toast.success("Ansatt oppdatert"); setOpen(false); onSaved(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><button data-testid={`edit-user-${user.id}`} className="text-muted-foreground hover:text-foreground"><Pencil className="w-4 h-4" /></button></DialogTrigger>
      <DialogContent className="bg-popover border-border">
        <DialogHeader><DialogTitle>Rediger ansatt</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input placeholder="Navn" data-testid="edit-user-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="bg-secondary border-border" />
          <PhoneInput testid="edit-user-phone" value={f.phone} onChange={(v) => setF({ ...f, phone: v })} />
          <div><Label className="label-caps">E-post (brukes til innlogging)</Label>
            <Input placeholder="E-post" data-testid="edit-user-email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="mt-1 bg-secondary border-border" /></div>
          <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}><SelectTrigger className="bg-secondary border-border" data-testid="edit-user-role"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-popover border-border">{Object.entries(ROLE_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent></Select>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="label-caps">Avdeling</Label>
              <Select value={f.department || ""} onValueChange={(v) => setF({ ...f, department: v })}><SelectTrigger className="mt-1 bg-secondary border-border" data-testid="edit-user-department"><SelectValue placeholder="Velg" /></SelectTrigger>
                <SelectContent className="bg-popover border-border">{deps.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent></Select></div>
            <div><Label className="label-caps">Lokasjon</Label>
              <Select value={f.location || ""} onValueChange={(v) => setF({ ...f, location: v })}><SelectTrigger className="mt-1 bg-secondary border-border" data-testid="edit-user-location"><SelectValue placeholder="Velg" /></SelectTrigger>
                <SelectContent className="bg-popover border-border">{locs.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent></Select></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="label-caps">Kapasitet (timer/uke)</Label><Input type="number" step="0.5" data-testid="edit-user-capacity" value={f.weekly_capacity} onChange={(e) => setF({ ...f, weekly_capacity: e.target.value })} className="mt-1 bg-secondary border-border" /></div>
            <div><Label className="label-caps">Årslønn (kr)</Label><Input type="number" step="1000" placeholder="F.eks. 650000" data-testid="edit-user-salary" value={f.annual_salary} onChange={(e) => setF({ ...f, annual_salary: e.target.value })} className="mt-1 bg-secondary border-border" /></div>
          </div>
          <p className="text-xs text-muted-foreground">Årslønn brukes til kostpris i lønnsomhetsrapporten: timepris = årslønn / 1920 × 1,3.</p>
          <Input placeholder="Nytt passord (valgfritt)" type="password" data-testid="edit-user-password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className="bg-secondary border-border" />
        </div>
        <DialogFooter><Button data-testid="edit-user-save" onClick={save} className="bg-white text-black hover:bg-white/90">Lagre endringer</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Users() {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [deps, setDeps] = useState([]);
  const [locs, setLocs] = useState([]);
  const [f, setF] = useState({ name: "", email: "", password: "", role: "accountant", department: "", location: "", phone: "", weekly_capacity: 37.5, annual_salary: "" });
  const load = () => api.get("/auth/users?scope=managed").then((r) => setUsers(r.data));
  useEffect(() => {
    load();
    api.get("/departments").then((r) => setDeps(r.data)).catch(() => {});
    api.get("/locations").then((r) => setLocs(r.data)).catch(() => {});
  }, []);
  const save = async () => {
    try {
      const payload = { ...f, weekly_capacity: Number(f.weekly_capacity) || 37.5, annual_salary: f.annual_salary === "" ? null : Number(f.annual_salary) };
      await api.post("/auth/users", payload); toast.success("Ansatt opprettet"); setOpen(false);
      setF({ name: "", email: "", password: "", role: "accountant", department: "", location: "", phone: "", weekly_capacity: 37.5, annual_salary: "" }); load();
    }
    catch (e) { toast.error(apiError(e)); }
  };
  const toggle = async (u) => { await api.put(`/auth/users/${u.id}`, { active: !u.active }); load(); };
  const del = async (u) => {
    if (!window.confirm(`Slette ${u.name} (${u.email}) permanent?`)) return;
    try { await api.delete(`/auth/users/${u.id}`); toast.success("Ansatt slettet"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="mt-4">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-muted-foreground">Interne ansatte/brukere med innlogging. Rollen styrer tilgang. Ansatte kan tildeles som oppdragsansvarlig på kundekortet.</p>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="new-user-btn" className="bg-white text-black hover:bg-white/90"><Plus className="w-4 h-4 mr-1" /> Ny ansatt</Button></DialogTrigger>
          <DialogContent className="bg-popover border-border">
            <DialogHeader><DialogTitle>Ny ansatt</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Input placeholder="Navn" data-testid="user-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="bg-secondary border-border" />
              <Input placeholder="E-post" data-testid="user-email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="bg-secondary border-border" />
              <PhoneInput testid="user-phone" value={f.phone} onChange={(v) => setF({ ...f, phone: v })} />
              <Input placeholder="Passord" type="password" data-testid="user-password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className="bg-secondary border-border" />
              <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}><SelectTrigger className="bg-secondary border-border" data-testid="user-role"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-popover border-border">{Object.entries(ROLE_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent></Select>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="label-caps">Avdeling</Label>
                  <Select value={f.department || ""} onValueChange={(v) => setF({ ...f, department: v })}><SelectTrigger className="mt-1 bg-secondary border-border" data-testid="user-department"><SelectValue placeholder="Velg" /></SelectTrigger>
                    <SelectContent className="bg-popover border-border">{deps.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent></Select></div>
                <div><Label className="label-caps">Lokasjon</Label>
                  <Select value={f.location || ""} onValueChange={(v) => setF({ ...f, location: v })}><SelectTrigger className="mt-1 bg-secondary border-border" data-testid="user-location"><SelectValue placeholder="Velg" /></SelectTrigger>
                    <SelectContent className="bg-popover border-border">{locs.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent></Select></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="label-caps">Kapasitet (timer/uke)</Label><Input type="number" step="0.5" data-testid="user-capacity" value={f.weekly_capacity} onChange={(e) => setF({ ...f, weekly_capacity: e.target.value })} className="mt-1 bg-secondary border-border" /></div>
                <div><Label className="label-caps">Årslønn (kr)</Label><Input type="number" step="1000" placeholder="F.eks. 650000" data-testid="user-salary" value={f.annual_salary} onChange={(e) => setF({ ...f, annual_salary: e.target.value })} className="mt-1 bg-secondary border-border" /></div>
              </div>
            </div>
            <DialogFooter><Button data-testid="user-save" onClick={save} className="bg-white text-black hover:bg-white/90">Lagre</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-border text-left">{["Navn", "E-post", "Telefon", "Rolle", "Avdeling", "Lokasjon", "Kap. t/uke", "Status", ""].map((h) => <th key={h} className="px-4 py-3 label-caps">{h}</th>)}</tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-b border-border/50">
                <td className="px-4 py-3 font-medium">{u.name}</td>
                <td className="px-4 py-3 text-muted-foreground">{u.email}</td>
                <td className="px-4 py-3 text-muted-foreground">{u.phone || "—"}</td>
                <td className="px-4 py-3">{ROLE_LABELS[u.role]}</td>
                <td className="px-4 py-3 text-muted-foreground">{u.department || "—"}</td>
                <td className="px-4 py-3 text-muted-foreground">{u.location || "—"}</td>
                <td className="px-4 py-3 text-muted-foreground">{u.weekly_capacity ?? 37.5}</td>
                <td className="px-4 py-3"><button onClick={() => toggle(u)} className={`text-xs px-2 py-1 rounded ${u.active ? "bg-emerald-500/15 text-emerald-400" : "bg-zinc-500/15 text-zinc-400"}`}>{u.active ? "Aktiv" : "Deaktivert"}</button></td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <EmployeeCard user={u} />
                    <EditUserDialog user={u} onSaved={load} deps={deps} locs={locs} />
                    <button data-testid={`delete-user-${u.id}`} onClick={() => del(u)} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Firm({ canEdit = true }) {
  const [f, setF] = useState({});
  useEffect(() => { api.get("/firm").then((r) => setF(r.data)); }, []);
  const save = async () => { await api.put("/firm", f); toast.success("Lagret"); };
  return (
    <div className="bg-card border border-border rounded-xl p-6 mt-4 max-w-lg">
      {!canEdit && <p className="text-xs text-muted-foreground mb-3">Kun administrator kan endre disse innstillingene.</p>}
      <fieldset disabled={!canEdit} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }} className="space-y-3">
        <div><Label className="label-caps">Foretaksnavn</Label><Input value={f.name || ""} onChange={(e) => setF({ ...f, name: e.target.value })} className="mt-1 bg-secondary border-border" /></div>
        <div><Label className="label-caps">Org.nr</Label><Input value={f.org_number || ""} onChange={(e) => setF({ ...f, org_number: e.target.value })} className="mt-1 bg-secondary border-border" /></div>
        <div><Label className="label-caps">Adresse</Label><Input value={f.address || ""} onChange={(e) => setF({ ...f, address: e.target.value })} className="mt-1 bg-secondary border-border" /></div>
        <div><Label className="label-caps">E-post</Label><Input value={f.email || ""} onChange={(e) => setF({ ...f, email: e.target.value })} className="mt-1 bg-secondary border-border" /></div>
        <div><Label className="label-caps">Daglig leder</Label><Input data-testid="firm-daglig-leder" value={f.daglig_leder || ""} onChange={(e) => setF({ ...f, daglig_leder: e.target.value })} className="mt-1 bg-secondary border-border" /><p className="text-xs text-muted-foreground mt-1">Vises som daglig leder i oppdragsavtalen (partsinformasjon).</p></div>
        <div><Label className="label-caps">Standard timepris (kr)</Label><Input type="number" data-testid="firm-hourly-rate" value={f.default_hourly_rate ?? ""} onChange={(e) => setF({ ...f, default_hourly_rate: Number(e.target.value) })} className="mt-1 bg-secondary border-border" /><p className="text-xs text-muted-foreground mt-1">Brukes til å estimere timebehov når kunden mangler egen timepris.</p></div>
        <Button data-testid="firm-save" onClick={save} className="bg-white text-black hover:bg-white/90">Lagre</Button>
      </fieldset>
    </div>
  );
}

function EmailTemplates({ canEdit = true }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/email-templates").then((r) => setRows(r.data)); }, []);
  const save = async (t) => { await api.put(`/email-templates/${t.key}`, t); toast.success("Mal lagret"); };
  return (
    <div className="space-y-4 mt-4">
      {!canEdit && <p className="text-xs text-muted-foreground">Kun administrator kan endre disse innstillingene.</p>}
      <fieldset disabled={!canEdit} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }} className="space-y-4">
      {rows.map((t, i) => (
        <div key={t.key} className="bg-card border border-border rounded-xl p-5">
          <p className="font-medium mb-2">{t.name}</p>
          <Input value={t.subject} onChange={(e) => { const cp = [...rows]; cp[i] = { ...t, subject: e.target.value }; setRows(cp); }} className="bg-secondary border-border mb-2" />
          <Textarea value={t.body} rows={3} onChange={(e) => { const cp = [...rows]; cp[i] = { ...t, body: e.target.value }; setRows(cp); }} className="bg-secondary border-border text-sm" />
          <Button size="sm" variant="secondary" className="mt-2" onClick={() => save(rows[i])}>Lagre</Button>
        </div>
      ))}
      </fieldset>
    </div>
  );
}

function RenewalConfig() {
  const [cfg, setCfg] = useState(null);
  useEffect(() => { api.get("/kyc/renewal-config").then((r) => setCfg(r.data)); }, []);
  const save = async () => { await api.put("/kyc/renewal-config", { intervals: cfg.intervals }); toast.success("Fornyelsesintervaller lagret"); };
  if (!cfg) return null;
  const labels = { lav: "Lav risiko", normal: "Normal risiko", forhoyet: "Forhøyet risiko", hoy: "Høy risiko", uakseptabel: "Uakseptabel" };
  return (
    <div className="bg-card border border-border rounded-xl p-6 mt-4 max-w-lg">
      <p className="text-sm text-muted-foreground mb-4">Antall måneder mellom hver KYC-kontroll per risikonivå. Systemet varsler automatisk 30 dager før forfall og oppretter oppgave for ansvarlig.</p>
      <div className="space-y-3">
        {Object.keys(labels).map((k) => (
          <div key={k} className="flex items-center justify-between gap-3">
            <Label className="text-sm">{labels[k]}</Label>
            <div className="flex items-center gap-2">
              <Input type="number" data-testid={`renewal-${k}`} value={cfg.intervals[k] ?? ""} onChange={(e) => setCfg({ ...cfg, intervals: { ...cfg.intervals, [k]: Number(e.target.value) } })} className="w-24 bg-secondary border-border" />
              <span className="text-sm text-muted-foreground">mnd</span>
            </div>
          </div>
        ))}
        <Button data-testid="renewal-save" onClick={save} className="bg-white text-black hover:bg-white/90 mt-2">Lagre</Button>
      </div>
    </div>
  );
}

function AuditLog() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/audit-log").then((r) => setRows(r.data)); }, []);
  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden mt-4">
      <table className="w-full text-sm">
        <thead><tr className="border-b border-border text-left">{["Tidspunkt", "Bruker", "Handling", "Objekt", "IP"].map((h) => <th key={h} className="px-4 py-3 label-caps">{h}</th>)}</tr></thead>
        <tbody>
          {rows.map((l) => (
            <tr key={l.id} className="border-b border-border/50">
              <td className="px-4 py-3 text-muted-foreground text-xs">{fmtDate(l.timestamp)}</td>
              <td className="px-4 py-3">{l.user_name}</td>
              <td className="px-4 py-3">{l.action}</td>
              <td className="px-4 py-3 text-muted-foreground">{l.entity_type}</td>
              <td className="px-4 py-3 text-muted-foreground text-xs">{l.ip || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DangerZone() {
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [wipeProducts, setWipeProducts] = useState(false);
  const [busy, setBusy] = useState(false);
  const reset = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/reset-data", { confirm, wipe_products: wipeProducts });
      if (!data.ok) { toast.error(data.detail || "Bekreftelse mangler"); return; }
      const n = Object.values(data.deleted).reduce((a, b) => a + b, 0);
      toast.success(`Nullstilt – ${n} poster slettet. Admin-brukere beholdt.`);
      setOpen(false); setConfirm("");
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="bg-card border border-red-500/30 rounded-xl p-6 mt-4 max-w-xl">
      <h3 className="font-display text-lg text-red-400">Nullstill data</h3>
      <p className="text-sm text-muted-foreground mt-2">
        Sletter <strong>alle kunder</strong> (inkl. KYC, risiko, avtaler, dokumenter, oppgaver, timer, aktiviteter og notater)
        og <strong>alle ansatte unntatt administratorer</strong>. Admin-brukere, produktkatalog og foretaksinnstillinger beholdes.
        Handlingen kan ikke angres.
      </p>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button variant="destructive" className="mt-4" data-testid="open-reset-btn"><Trash2 className="w-4 h-4 mr-1" /> Nullstill alt</Button>
        </DialogTrigger>
        <DialogContent className="bg-popover border-border">
          <DialogHeader>
            <DialogTitle>Bekreft nullstilling</DialogTitle>
            <DialogDescription>Skriv <strong>SLETT</strong> for å bekrefte. Alle kunder og ikke-admin-ansatte fjernes permanent.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Skriv SLETT" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="bg-secondary border-border" data-testid="reset-confirm-input" />
            <label className="flex items-center gap-2 text-sm"><Checkbox checked={wipeProducts} onCheckedChange={setWipeProducts} data-testid="reset-wipe-products" /> Slett også produktkatalog og pakker</label>
          </div>
          <DialogFooter>
            <Button variant="destructive" onClick={reset} disabled={busy || confirm !== "SLETT"} data-testid="confirm-reset-btn">{busy ? "Sletter…" : "Slett permanent"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ListEditor({ title, endpoint, usagePath, deletePath, renamePath, kind, testidPrefix, placeholder, help, canEdit = true }) {
  const [items, setItems] = useState([]);
  const [val, setVal] = useState("");
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [pending, setPending] = useState(null);
  const [usage, setUsage] = useState(null);
  const [target, setTarget] = useState("__none__");
  const [renaming, setRenaming] = useState(null);
  const [renameVal, setRenameVal] = useState("");
  useEffect(() => { api.get(endpoint).then((r) => setItems(r.data || [])).catch(() => {}).finally(() => setLoaded(true)); }, [endpoint]);

  const openRename = (x) => { setRenaming(x); setRenameVal(x); };
  const confirmRename = async () => {
    const nv = renameVal.trim();
    if (!nv) return;
    if (nv === renaming) { setRenaming(null); return; }
    setBusy(true);
    try { const { data } = await api.put(renamePath, { old: renaming, new: nv }); setItems(data); setRenaming(null); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const add = async () => {
    const v = val.trim();
    if (!v || !loaded) return;
    if (items.some((x) => x.toLowerCase() === v.toLowerCase())) { toast.error("Finnes allerede"); return; }
    setBusy(true);
    try { const { data } = await api.put(endpoint, [...items, v]); setItems(data); setVal(""); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const openDelete = async (x) => {
    if (!loaded) return;
    setPending(x); setUsage(null); setTarget("__none__");
    try {
      const { data } = await api.get(`${usagePath}?name=${encodeURIComponent(x)}`);
      setUsage(data);
      const total = (data.customers || 0) + (data.employees || 0) + (data.products || 0);
      const others = items.filter((i) => i !== x);
      setTarget(total > 0 && others.length ? others[0] : "__none__");
    } catch { setUsage({}); }
  };

  const total = usage ? (usage.customers || 0) + (usage.employees || 0) + (usage.products || 0) : 0;
  const summary = () => {
    if (!usage) return "";
    const p = [];
    if (usage.customers) p.push(`${usage.customers} ${usage.customers === 1 ? "kunde" : "kunder"}`);
    if (usage.employees) p.push(`${usage.employees} ${usage.employees === 1 ? "ansatt" : "ansatte"}`);
    if (usage.products) p.push(`${usage.products} ${usage.products === 1 ? "produkt" : "produkter"}`);
    return p.join(", ");
  };

  const confirmDelete = async () => {
    setBusy(true);
    try {
      const tgt = target === "__none__" ? "" : target;
      const { data } = await api.delete(`${deletePath}?name=${encodeURIComponent(pending)}&target=${encodeURIComponent(tgt)}`);
      setItems(data);
      setPending(null);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const others = items.filter((i) => i !== pending);
  const whoLabel = kind === "location" ? "kunder eller ansatte" : kind === "position" ? "ansatte" : "kunder, ansatte eller produkter";

  return (
    <div className="rounded-lg border border-border bg-card p-5" data-testid={`${testidPrefix}-editor`}>
      <h3 className="font-display text-lg">{title}</h3>
      {help && <p className="text-xs text-muted-foreground mt-1 max-w-xl">{help}</p>}
      {!canEdit && <p className="text-xs text-muted-foreground mt-2">Kun administrator kan endre disse innstillingene.</p>}
      <fieldset disabled={!canEdit} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
      <div className="flex flex-wrap gap-2 mt-4">
        {items.map((x) => (
          <span key={x} data-testid={`${testidPrefix}-item-${x}`} className="inline-flex items-center gap-1 text-sm bg-secondary border border-border rounded-full pl-3 pr-1 py-1">
            {x}
            <button onClick={() => openRename(x)} disabled={busy || !loaded} data-testid={`${testidPrefix}-rename-${x}`} className="rounded-full hover:bg-white/10 p-0.5 text-muted-foreground hover:text-foreground"><Pencil className="w-3 h-3" /></button>
            <button onClick={() => openDelete(x)} disabled={busy || !loaded} data-testid={`${testidPrefix}-del-${x}`} className="rounded-full hover:bg-white/10 p-0.5 text-muted-foreground hover:text-foreground"><X className="w-3.5 h-3.5" /></button>
          </span>
        ))}
        {loaded && items.length === 0 && <span className="text-sm text-muted-foreground">Ingen ennå</span>}
      </div>
      <div className="flex gap-2 mt-4 max-w-md">
        <Input value={val} onChange={(e) => setVal(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder={placeholder} data-testid={`${testidPrefix}-input`} className="bg-secondary border-border" />
        <Button onClick={add} disabled={busy || !loaded || !val.trim()} data-testid={`${testidPrefix}-add`} className="bg-white text-black hover:bg-white/90 shrink-0"><Plus className="w-4 h-4 mr-1" /> Legg til</Button>
      </div>
      </fieldset>

      <Dialog open={!!pending} onOpenChange={(o) => !o && setPending(null)}>
        <DialogContent className="bg-card border-border" data-testid={`${testidPrefix}-delete-dialog`}>
          <DialogHeader>
            <DialogTitle>Fjern «{pending}»</DialogTitle>
            <DialogDescription>
              {usage === null ? "Sjekker bruk …"
                : total === 0 ? `Ingen ${whoLabel} bruker denne. Du kan trygt fjerne den.`
                  : `${summary()} bruker denne. Velg hvor de skal flyttes før den fjernes.`}
            </DialogDescription>
          </DialogHeader>
          {total > 0 && (
            <div>
              <Label className="label-caps">Flytt til</Label>
              <Select value={target} onValueChange={setTarget}>
                <SelectTrigger className="mt-1.5 bg-secondary border-border" data-testid={`${testidPrefix}-move-target`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {others.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                  <SelectItem value="__none__">— Fjern uten å flytte (tøm feltet) —</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPending(null)} className="border-border">Avbryt</Button>
            <Button onClick={confirmDelete} disabled={busy || usage === null} data-testid={`${testidPrefix}-confirm-del`} className="bg-red-600 text-white hover:bg-red-700">Fjern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!renaming} onOpenChange={(o) => !o && setRenaming(null)}>
        <DialogContent className="bg-card border-border" data-testid={`${testidPrefix}-rename-dialog`}>
          <DialogHeader>
            <DialogTitle>Gi nytt navn</DialogTitle>
            <DialogDescription>Navneendringen oppdaterer også alle som allerede bruker «{renaming}».</DialogDescription>
          </DialogHeader>
          <div>
            <Label className="label-caps">Nytt navn</Label>
            <Input value={renameVal} onChange={(e) => setRenameVal(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); confirmRename(); } }}
              data-testid={`${testidPrefix}-rename-input`} className="mt-1 bg-secondary border-border" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenaming(null)} className="border-border">Avbryt</Button>
            <Button onClick={confirmRename} disabled={busy || !renameVal.trim()} data-testid={`${testidPrefix}-rename-save`} className="bg-white text-black hover:bg-white/90">Lagre</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PositionsEditor({ canEdit = true }) {
  const [items, setItems] = useState([]);
  const [areas, setAreas] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState("");
  const [area, setArea] = useState("__none__");
  const [edit, setEdit] = useState(null);
  const [editName, setEditName] = useState("");
  const [editArea, setEditArea] = useState("__none__");
  const [del, setDel] = useState(null);
  const [usage, setUsage] = useState(null);
  const [moveTo, setMoveTo] = useState("__none__");

  useEffect(() => {
    api.get("/positions").then((r) => setItems(r.data || [])).catch(() => {}).finally(() => setLoaded(true));
    api.get("/departments").then((r) => setAreas(r.data || [])).catch(() => {});
  }, []);

  const add = async () => {
    const n = name.trim();
    if (!n || !loaded) return;
    if (items.some((p) => p.name.toLowerCase() === n.toLowerCase())) { toast.error("Finnes allerede"); return; }
    setBusy(true);
    try { const { data } = await api.put("/positions", [...items, { name: n, area: area === "__none__" ? "" : area }]); setItems(data); setName(""); setArea("__none__"); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const openEdit = (p) => { setEdit(p); setEditName(p.name); setEditArea(p.area || "__none__"); };
  const saveEdit = async () => {
    const nn = editName.trim();
    if (!nn) return;
    setBusy(true);
    try { const { data } = await api.put("/admin/positions/update", { old_name: edit.name, new_name: nn, area: editArea === "__none__" ? "" : editArea }); setItems(data); setEdit(null); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const openDelete = async (p) => {
    setDel(p); setUsage(null); setMoveTo("__none__");
    try {
      const { data } = await api.get(`/admin/position-usage?name=${encodeURIComponent(p.name)}`);
      setUsage(data);
      const others = items.filter((x) => x.name !== p.name);
      setMoveTo((data.employees || 0) > 0 && others.length ? others[0].name : "__none__");
    } catch { setUsage({}); }
  };
  const confirmDelete = async () => {
    setBusy(true);
    try {
      const tgt = moveTo === "__none__" ? "" : moveTo;
      const { data } = await api.delete(`/admin/positions?name=${encodeURIComponent(del.name)}&target=${encodeURIComponent(tgt)}`);
      setItems(data); setDel(null);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const groups = {};
  items.forEach((p) => { const a = p.area || "Uten område"; (groups[a] = groups[a] || []).push(p); });
  const groupNames = Object.keys(groups).sort((a, b) => a === "Uten område" ? 1 : b === "Uten område" ? -1 : a.localeCompare(b, "nb"));
  const others = del ? items.filter((x) => x.name !== del.name) : [];

  return (
    <div className="rounded-lg border border-border bg-card p-5" data-testid="pos-editor">
      <h3 className="font-display text-lg">Stillinger</h3>
      <p className="text-xs text-muted-foreground mt-1 max-w-xl">Stillinger kan velges som stillingstittel på ansattkortet og knyttes til et forretningsområde (gruppert nedenfor). Når du fjerner en stilling kan du flytte berørte ansatte til en annen.</p>
      {!canEdit && <p className="text-xs text-muted-foreground mt-2">Kun administrator kan endre disse innstillingene.</p>}
      <fieldset disabled={!canEdit} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
      <div className="mt-4 space-y-4">
        {loaded && items.length === 0 && <span className="text-sm text-muted-foreground">Ingen ennå</span>}
        {groupNames.map((g) => (
          <div key={g}>
            <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1.5">{g}</p>
            <div className="flex flex-wrap gap-2">
              {groups[g].map((p) => (
                <span key={p.name} data-testid={`pos-item-${p.name}`} className="inline-flex items-center gap-1 text-sm bg-secondary border border-border rounded-full pl-3 pr-1 py-1">
                  {p.name}
                  <button onClick={() => openEdit(p)} data-testid={`pos-edit-${p.name}`} className="rounded-full hover:bg-white/10 p-0.5 text-muted-foreground hover:text-foreground"><Pencil className="w-3 h-3" /></button>
                  <button onClick={() => openDelete(p)} data-testid={`pos-del-${p.name}`} className="rounded-full hover:bg-white/10 p-0.5 text-muted-foreground hover:text-foreground"><X className="w-3.5 h-3.5" /></button>
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-2 mt-4 items-end">
        <div className="flex-1 min-w-[180px]">
          <Label className="label-caps">Ny stilling</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} placeholder="f.eks. Regnskapsfører" data-testid="pos-input" className="mt-1 bg-secondary border-border" />
        </div>
        <div className="w-48">
          <Label className="label-caps">Forretningsområde</Label>
          <Select value={area} onValueChange={setArea}>
            <SelectTrigger className="mt-1 bg-secondary border-border" data-testid="pos-area"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">Ingen</SelectItem>
              {areas.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={add} disabled={busy || !loaded || !name.trim()} data-testid="pos-add" className="bg-white text-black hover:bg-white/90"><Plus className="w-4 h-4 mr-1" /> Legg til</Button>
      </div>
      </fieldset>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="bg-card border-border" data-testid="pos-edit-dialog">
          <DialogHeader>
            <DialogTitle>Endre stilling</DialogTitle>
            <DialogDescription>Endre navn og/eller forretningsområde. Navneendring oppdaterer også ansatte med denne tittelen.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label className="label-caps">Navn</Label>
              <Input value={editName} onChange={(e) => setEditName(e.target.value)} data-testid="pos-edit-name" className="mt-1 bg-secondary border-border" /></div>
            <div><Label className="label-caps">Forretningsområde</Label>
              <Select value={editArea} onValueChange={setEditArea}>
                <SelectTrigger className="mt-1 bg-secondary border-border" data-testid="pos-edit-area"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Ingen</SelectItem>
                  {areas.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                </SelectContent>
              </Select></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEdit(null)} className="border-border">Avbryt</Button>
            <Button onClick={saveEdit} disabled={busy || !editName.trim()} data-testid="pos-edit-save" className="bg-white text-black hover:bg-white/90">Lagre</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <DialogContent className="bg-card border-border" data-testid="pos-delete-dialog">
          <DialogHeader>
            <DialogTitle>Fjern «{del?.name}»</DialogTitle>
            <DialogDescription>
              {usage === null ? "Sjekker bruk …"
                : (usage.employees || 0) === 0 ? "Ingen ansatte bruker denne. Du kan trygt fjerne den."
                  : `${usage.employees} ${usage.employees === 1 ? "ansatt" : "ansatte"} bruker denne. Velg hvor de skal flyttes før den fjernes.`}
            </DialogDescription>
          </DialogHeader>
          {usage && (usage.employees || 0) > 0 && (
            <div><Label className="label-caps">Flytt til</Label>
              <Select value={moveTo} onValueChange={setMoveTo}>
                <SelectTrigger className="mt-1.5 bg-secondary border-border" data-testid="pos-move-target"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {others.map((o) => <SelectItem key={o.name} value={o.name}>{o.name}</SelectItem>)}
                  <SelectItem value="__none__">— Fjern uten å flytte (tøm feltet) —</SelectItem>
                </SelectContent>
              </Select></div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDel(null)} className="border-border">Avbryt</Button>
            <Button onClick={confirmDelete} disabled={busy || usage === null} data-testid="pos-confirm-del" className="bg-red-600 text-white hover:bg-red-700">Fjern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Organisasjon({ canEdit = true }) {
  return (
    <div className="space-y-5">
      <ListEditor title="Lokasjoner" endpoint="/locations" usagePath="/admin/location-usage" deletePath="/admin/locations" renamePath="/admin/locations/rename" kind="location" testidPrefix="loc"
        placeholder="Ny lokasjon (f.eks. Oslo)"
        help="Lokasjoner kan velges på kunder og ansatte, og brukes i rapporter per lokasjon." canEdit={canEdit} />
      <ListEditor title="Forretningsområder" endpoint="/departments" usagePath="/admin/department-usage" deletePath="/admin/departments" renamePath="/admin/departments/rename" kind="department" testidPrefix="area"
        placeholder="Nytt forretningsområde"
        help="Forretningsområder brukes som avdeling(er) på kunder og ansatte, og som kategori i produktkatalogen. Du kan gi nytt navn (oppdaterer alle) eller fjerne og flytte berørte kunder, ansatte og produkter." canEdit={canEdit} />
      <PositionsEditor canEdit={canEdit} />
    </div>
  );
}

export default function Admin() {
  const [me, setMe] = useState(null);
  useEffect(() => { api.get("/auth/me").then((r) => setMe(r.data)).catch(() => {}); }, []);
  const isManager = !!me?.is_people_manager;
  const isAdmin = me?.role === "admin";
  const defaultTab = isManager ? "brukere" : "fornyelse";
  return (
    <Page>
      <PageHeader title="Administrasjon" subtitle="Brukere, foretak, maler og revisjonslogg" testid="admin-header" />
      {me && (
      <Tabs defaultValue={defaultTab}>
        <TabsList className="bg-secondary flex-wrap h-auto">
          {isManager && <TabsTrigger value="brukere" data-testid="tab-brukere">Ansatte</TabsTrigger>}
          {isManager && <TabsTrigger value="foretak" data-testid="tab-foretak">Foretak</TabsTrigger>}
          {isManager && <TabsTrigger value="organisasjon" data-testid="tab-organisasjon">Lokasjoner & områder</TabsTrigger>}
          {isManager && <TabsTrigger value="maler" data-testid="tab-maler">E-postmaler</TabsTrigger>}
          <TabsTrigger value="fornyelse" data-testid="tab-fornyelse">KYC-fornyelse</TabsTrigger>
          <TabsTrigger value="revisjon" data-testid="tab-revisjon">Revisjonslogg</TabsTrigger>
          {isAdmin && <TabsTrigger value="nullstill" data-testid="tab-nullstill">Nullstill data</TabsTrigger>}
        </TabsList>
        {isManager && <TabsContent value="brukere"><Users /></TabsContent>}
        {isManager && <TabsContent value="foretak"><Firm canEdit={isAdmin} /></TabsContent>}
        {isManager && <TabsContent value="organisasjon"><Organisasjon canEdit={isAdmin} /></TabsContent>}
        {isManager && <TabsContent value="maler"><EmailTemplates canEdit={isAdmin} /></TabsContent>}
        <TabsContent value="fornyelse"><RenewalConfig /></TabsContent>
        <TabsContent value="revisjon"><AuditLog /></TabsContent>
        {isAdmin && <TabsContent value="nullstill"><DangerZone /></TabsContent>}
      </Tabs>
      )}
    </Page>
  );
}
